import React, { useState, useEffect } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Container,
} from "@mui/material";

import GeneralLayout from "components/PageLayouts/GeneralLayout";

import { AccountType } from "api/Accounts/AccountsInterfaces";

import { organisationAPI } from "api/Accounts/AccountsAPI";
import { Organisation as OrganisationType } from "api/Accounts/AccountsInterfaces";

import GenericRecordSummary from "components/Records/RecordDisplay/GenericRecordSummary";

import AccountTypeCarousel from "components/AccountPricing/AccountTypeCarousel";
import BasePaper from "components/Flow/Nodes/StandardNode/StandardNodeFunctionality/BasePaper";

const Accounts: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [organisation, setOrganisation] = useState<OrganisationType | null>(null);
  const [organisationID, setOrganisationID] = useState<number | null>(null);
  
  // Popup dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAccountType, setPendingAccountType] = useState<AccountType | null>(null);
  
  useEffect(() => {
    const fetchOrganisation = async () => {
      try {
        const org = await organisationAPI.getByLoggedInUser();
        setOrganisation(org);
        setOrganisationID(Number(org.id));
      } catch (error) {
        console.error("Failed to fetch organisation:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganisation();
  }, []);

  const onUpdate = async (
    id: number,
    field: keyof OrganisationType,
    value: any
  ): Promise<void> => {
    await organisationAPI.updateField(id, field, value);

    // After updating, refetch organisation
    const refreshed = await organisationAPI.getById(id);
    setOrganisation(refreshed);
  };

  const handleAccountTypeSelect = (selectedAccountType: AccountType) => {
    setPendingAccountType(selectedAccountType);
    setConfirmOpen(true); // Open the confirmation popup
  };

  const confirmChange = async () => {
    if (!pendingAccountType) return;

    await onUpdate(Number(organisationID), "account_type", pendingAccountType.id);

    setConfirmOpen(false);
    setPendingAccountType(null);
  };
  
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!organisation) {
    return <div>Organisation not found</div>;
  }

  return (
    <GeneralLayout>
      <GenericRecordSummary
        record={organisation}
        titleField="name"
        descriptionField="description"
        onUpdate={onUpdate}
        loading={loading}
      />
      <Box sx={{ mx: 2, my: 2 }}>
        <BasePaper>
          {/* Account Type Carousel */}
            <AccountTypeCarousel
              title="Change Your Plan"
              excludeIds={[1, 2, 3, 4]}
              selectedId={organisation.account_type}   // 🔥 pre-select current plan
              onSelect={handleAccountTypeSelect}
            />
        </BasePaper>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Account Type Change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingAccountType
              ? `Are you sure you want to change your organisation’s account type to "${pendingAccountType.name}"?`
              : "Are you sure you want to make this change?"}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={confirmChange}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </GeneralLayout>
  );
};

export default Accounts;
